/**
 * 契約・単価変更・期限アラートの型と入力検証（02 5.3.13 contract）。
 * - 契約コードは自動採番だけ。要員は登録のときだけ（付け替えない）
 * - 単価（sales_unit_price・supplier_unit_price）は登録のときだけ。変えるのは API-CNTR-08（理由必須・履歴に残す：要件書 2.3）。
 *   更新の本文に単価・要員を入れると定義外の項目として 400 VALIDATION_001（details の field に項目名：03 BE-15）
 * - 項目をまたぐ決まり：終了日は開始日以降（FE-MSG-V-006）・稼働時間の上限は下限以上（FE-MSG-V-005）＝ contractRuleIssues（画面と BE で同じ関数）。
 *   「終了（ended）は終了日が JST の今日以前のときだけ」は BE では 409 STATE_001、画面では FE-MSG-V-007 で先に止める
 * - 金額・時間は Decimal 文字列（02 2.11）。比べるのは shared/money の compareMoney だけ（02 11.2 No.5）
 */
import { z } from 'zod';
import { compareDate, isValidJstDate } from '../date/jst';
import { CONTRACT_STATUS_VALUES } from '../enums/contract-status';
import { CONTRACT_TYPE_VALUES } from '../enums/contract-type';
import { CUSTOMER_TYPE_VALUES } from '../enums/customer-type';
import { PRICE_CHANGE_TYPE_VALUES } from '../enums/price-change-type';
import { ROUNDING_TYPE_VALUES } from '../enums/rounding-type';
import { compareMoney, isDecimalString } from '../money/decimal';
import {
  zCode,
  zDate,
  zDayOfMonth,
  zEnum,
  zHours,
  zMoney,
  zRequiredText,
  zShortText,
  zSystemCode,
  zVersion,
} from './common.schema';
import { zNullable } from './customer.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

/** 登録と更新で同じ項目（要員・単価を除く） */
const CONTRACT_EDITABLE_SHAPE = {
  contract_type: zEnum(CONTRACT_TYPE_VALUES),
  contract_status: zEnum(CONTRACT_STATUS_VALUES).optional(),
  start_date: zDate,
  end_date: zNullable(zDate).optional(),
  sales_customer_code: zCode,
  sales_customer_contact_code: zNullable(zSystemCode).optional(),
  sales_closing_day: zDayOfMonth,
  sales_payment_day: zDayOfMonth,
  payment_site: zRequiredText(50),
  supplier_customer_code: zNullable(zCode).optional(),
  supplier_customer_contact_code: zNullable(zSystemCode).optional(),
  supplier_closing_day: zNullable(zDayOfMonth).optional(),
  supplier_payment_day: zNullable(zDayOfMonth).optional(),
  minimum_work_hours: zHours,
  maximum_work_hours: zHours,
  rounding_type: zEnum(ROUNDING_TYPE_VALUES).optional(),
  sales_overtime_rate: zNullable(zMoney).optional(),
  supplier_overtime_rate: zNullable(zMoney).optional(),
  sales_deduction_rate: zNullable(zMoney).optional(),
  supplier_deduction_rate: zNullable(zMoney).optional(),
  sales_support_fee: zNullable(zMoney).optional(),
  sales_user_code: zNullable(zSystemCode).optional(),
  /** 備考（274：旧PCRM の契約の備考 notes。任意の文字列・上限は要員の note と同じ 2000） */
  note: zShortText(2000).optional(),
};

export const CONTRACT_INPUT_SHAPE = {
  personnel_code: zCode,
  ...CONTRACT_EDITABLE_SHAPE,
  sales_unit_price: zMoney,
  supplier_unit_price: zNullable(zMoney).optional(),
};

export interface ContractRuleValues {
  start_date?: string | null | undefined;
  end_date?: string | null | undefined;
  minimum_work_hours?: string | null | undefined;
  maximum_work_hours?: string | null | undefined;
  contract_status?: string | null | undefined;
}

export interface ContractRuleIssue {
  field: 'end_date' | 'maximum_work_hours' | 'contract_status';
  code: 'FE-MSG-V-005' | 'FE-MSG-V-006' | 'FE-MSG-V-007';
}

/**
 * 項目をまたぐ決まり（02 5.3.13・03 3.6.3）。today を渡したときだけ「終了は終了日が今日以前」（FE-MSG-V-007）も見る（画面用）。
 */
export function contractRuleIssues(values: ContractRuleValues, today?: string): ContractRuleIssue[] {
  const issues: ContractRuleIssue[] = [];
  if (
    values.start_date &&
    values.end_date &&
    isValidJstDate(values.start_date) &&
    isValidJstDate(values.end_date) &&
    compareDate(values.end_date, values.start_date) < 0
  ) {
    issues.push({ field: 'end_date', code: 'FE-MSG-V-006' });
  }
  if (
    values.minimum_work_hours &&
    values.maximum_work_hours &&
    isDecimalString(values.minimum_work_hours) &&
    isDecimalString(values.maximum_work_hours) &&
    compareMoney(values.maximum_work_hours, values.minimum_work_hours) < 0
  ) {
    issues.push({ field: 'maximum_work_hours', code: 'FE-MSG-V-005' });
  }
  if (today !== undefined && values.contract_status === 'ended' && !isEndedAllowed(values.end_date, today)) {
    issues.push({ field: 'contract_status', code: 'FE-MSG-V-007' });
  }
  return issues;
}

/** 「終了」にできるのは終了日があり JST の今日以前のとき（要件書 2.3） */
export function isEndedAllowed(end_date: string | null | undefined, today: string): boolean {
  return Boolean(end_date) && isValidJstDate(end_date as string) && compareDate(end_date as string, today) <= 0;
}

/** 「終了」から 稼働中・契約前・中断 に戻せるのは終了日が無いか JST の今日以降のとき（02 5.3.13。274 で戻し先を 4 値に広げた） */
export function isActiveAllowed(end_date: string | null | undefined, today: string): boolean {
  return !end_date || compareDate(end_date, today) >= 0;
}

/** API-CNTR-02 契約の登録 */
export const ContractCreateSchema = z
  .object(CONTRACT_INPUT_SHAPE)
  .strict()
  .superRefine((values, context) => {
    for (const issue of contractRuleIssues(values)) {
      context.addIssue({ code: 'custom', path: [issue.field], message: issue.code });
    }
  });

/** API-CNTR-04 契約の更新（要員・単価を除く項目がすべて任意＋version。項目をまたぐ決まりは保存済みの値と合わせて service で確かめる） */
export const ContractUpdateSchema = z
  .object({
    ...CONTRACT_EDITABLE_SHAPE,
    contract_type: CONTRACT_EDITABLE_SHAPE.contract_type.optional(),
    start_date: CONTRACT_EDITABLE_SHAPE.start_date.optional(),
    sales_customer_code: CONTRACT_EDITABLE_SHAPE.sales_customer_code.optional(),
    sales_closing_day: CONTRACT_EDITABLE_SHAPE.sales_closing_day.optional(),
    sales_payment_day: CONTRACT_EDITABLE_SHAPE.sales_payment_day.optional(),
    payment_site: CONTRACT_EDITABLE_SHAPE.payment_site.optional(),
    minimum_work_hours: CONTRACT_EDITABLE_SHAPE.minimum_work_hours.optional(),
    maximum_work_hours: CONTRACT_EDITABLE_SHAPE.maximum_work_hours.optional(),
    version: zVersion,
  })
  .strict();

/** API-CNTR-08 単価変更の登録（新しい単価を省略したらその単価は変えない） */
export const PriceChangeCreateSchema = z
  .object({
    effective_from: zDate.optional(),
    new_sales_unit_price: zMoney.optional(),
    new_supplier_unit_price: zMoney.optional(),
    change_reason: zRequiredText(500, 'MSG-V-088'),
    version: zVersion,
  })
  .strict();

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

export const ContractSchema = z
  .object({
    contract_code: z.string(),
    personnel_code: z.string(),
    personnel_name: z.string().nullable(),
    /** 要員が削除済み（02 2.9「<参照先>_deleted」） */
    personnel_deleted: z.boolean(),
    contract_type: z.enum(CONTRACT_TYPE_VALUES),
    contract_status: z.enum(CONTRACT_STATUS_VALUES),
    start_date: z.string(),
    end_date: z.string().nullable(),
    sales_customer_code: z.string(),
    sales_customer_name: z.string().nullable(),
    sales_customer_type: z.enum(CUSTOMER_TYPE_VALUES).nullable(),
    sales_customer_deleted: z.boolean(),
    sales_customer_contact_code: z.string().nullable(),
    sales_customer_contact_name: z.string().nullable(),
    sales_customer_contact_department: z.string().nullable(),
    sales_unit_price: z.string(),
    sales_closing_day: z.number().int(),
    sales_payment_day: z.number().int(),
    payment_site: z.string(),
    supplier_customer_code: z.string().nullable(),
    supplier_customer_name: z.string().nullable(),
    supplier_customer_type: z.enum(CUSTOMER_TYPE_VALUES).nullable(),
    supplier_customer_deleted: z.boolean(),
    supplier_customer_contact_code: z.string().nullable(),
    supplier_customer_contact_name: z.string().nullable(),
    supplier_unit_price: z.string().nullable(),
    supplier_closing_day: z.number().int().nullable(),
    supplier_payment_day: z.number().int().nullable(),
    minimum_work_hours: z.string(),
    maximum_work_hours: z.string(),
    rounding_type: z.enum(ROUNDING_TYPE_VALUES),
    sales_overtime_rate: z.string().nullable(),
    supplier_overtime_rate: z.string().nullable(),
    sales_deduction_rate: z.string().nullable(),
    supplier_deduction_rate: z.string().nullable(),
    sales_support_fee: z.string().nullable(),
    sales_user_code: z.string().nullable(),
    sales_user_name: z.string().nullable(),
    /** 備考（274） */
    note: z.string().nullable(),
    gross_profit: z.string(),
    profit_rate: z.string().nullable(),
    /** 詳細（API-CNTR-03・08 の応答）だけ。一覧では null */
    price_change_count: z.number().int().nullable(),
    monthly_checklist_year_months: z.array(z.string()).nullable(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

export const PriceChangeSchema = z.object({
  contract_code: z.string(),
  effective_from: z.string(),
  price_change_type: z.enum(PRICE_CHANGE_TYPE_VALUES),
  old_sales_unit_price: z.string(),
  new_sales_unit_price: z.string(),
  old_supplier_unit_price: z.string().nullable(),
  new_supplier_unit_price: z.string().nullable(),
  change_reason: z.string(),
  changed_by: z.string(),
  changed_by_name: z.string().nullable(),
  changed_at: z.string(),
  old_gross_profit: z.string(),
  new_gross_profit: z.string(),
  new_profit_rate: z.string().nullable(),
  /** 適用開始日が JST の今日より後（まだ契約の単価に入っていない：D-051） */
  is_scheduled: z.boolean(),
});

export const PriceChangeResponseSchema = z.object({ price_change: PriceChangeSchema, contract: ContractSchema });

export const ContractAlertSchema = z.object({
  contract_code: z.string(),
  personnel_code: z.string(),
  personnel_name: z.string().nullable(),
  contract_type: z.enum(CONTRACT_TYPE_VALUES),
  sales_customer_code: z.string(),
  sales_customer_name: z.string().nullable(),
  supplier_customer_name: z.string().nullable(),
  end_date: z.string(),
  days_remaining: z.number().int(),
  sales_user_code: z.string().nullable(),
  sales_user_name: z.string().nullable(),
});

export const CONTRACT_LIST_SORT_VALUES = ['contract_code', 'start_date', 'gross_profit', 'created_at'] as const;

/** API-CNTR-01（02 2.3.2） */
export const ContractListQuerySchema = buildListQuerySchema(CONTRACT_LIST_SORT_VALUES, 'contract_code').extend({
  personnel_code: zCode.optional(),
  sales_customer_code: zCode.optional(),
  supplier_customer_code: zCode.optional(),
  contract_status: zEnum(CONTRACT_STATUS_VALUES).optional(),
  contract_type: zEnum(CONTRACT_TYPE_VALUES).optional(),
  start_date_from: zDate.optional(),
  start_date_to: zDate.optional(),
});

/** 期限アラートの日数（1〜180。既定 30：D-052） */
export const CONTRACT_ALERT_DEFAULT_DAYS = 30;

/** API-CNTR-10（02 5.3.13） */
export const ContractAlertQuerySchema = z.object({
  within_days: z.coerce
    .number({ message: 'MSG-V-020' })
    .int('MSG-V-020')
    .min(1, 'MSG-V-020')
    .max(180, 'MSG-V-020')
    .default(CONTRACT_ALERT_DEFAULT_DAYS),
});

export type ContractCreateInput = z.infer<typeof ContractCreateSchema>;
export type ContractUpdateInput = z.infer<typeof ContractUpdateSchema>;
export type PriceChangeCreateInput = z.infer<typeof PriceChangeCreateSchema>;
export type Contract = z.infer<typeof ContractSchema>;
export type PriceChange = z.infer<typeof PriceChangeSchema>;
export type PriceChangeResponse = z.infer<typeof PriceChangeResponseSchema>;
export type ContractAlert = z.infer<typeof ContractAlertSchema>;
export type ContractListQuery = z.infer<typeof ContractListQuerySchema>;
export type ContractAlertQuery = z.infer<typeof ContractAlertQuerySchema>;
